import React, { useState, useRef, useEffect } from 'react';
import {
	Container,
	Paper,
	Typography,
	Box,
	Grid,
	Card,
	TextField,
	Button,
	Avatar,
	IconButton,
	Slide,
	Divider,
	Chip,
	alpha,
	useTheme,
	FormControl,
	FormControlLabel,
	Radio,
	RadioGroup,
	Checkbox,
	FormGroup,
	CardContent,
	DialogTitle,
	DialogContent,
	DialogActions,
	Dialog
} from '@mui/material';
import {
	Psychology as PsychologyIcon,
	Favorite as FavoriteIcon,
	FavoriteBorder as FavoriteBorderIcon,
	NightlightRound as NightlightRoundIcon,
	SelfImprovement as SelfImprovementIcon,
	Work as WorkIcon,
	Groups as GroupsIcon,
	EmojiPeople as EmojiPeopleIcon,
	Healing as HealingIcon,
	SmartToy as SmartToyIcon,
	Send as SendIcon,
	ArrowBack as ArrowBackIcon,
	Mood as MoodIcon,
	SentimentDissatisfied as SentimentDissatisfiedIcon,
	Bedtime as BedtimeIcon,
	MoodBad as MoodBadIcon,
	EmojiEmotions as EmojiEmotionsIcon,
	PeopleAlt as PeopleAltIcon,
	MoreHoriz as MoreHorizIcon,
	AccessTime as AccessTimeIcon
} from '@mui/icons-material';
import { keyframes } from '@emotion/react';
import questionsData from './chatbot_question.json';
import messageStructureData from './topic_message_structure.json';
import questionsResponseData from './chatbot_question_response.json';
import answersData from './chatbot_answer.json';
import closingStatementsData from './chatbot_closing_statement.json';
import comfortStatementsData from './chatbot_comfort_statement.json';
import { useNavigate } from 'react-router-dom';

const categories = [
	{
		name: 'Burnout',
		icon: <FavoriteBorderIcon />,
		color: '#4C41FF',
		description: 'Feeling exhausted and overwhelmed by work or life demands?',
		package: 'both'
	},
	{
		name: 'Stress',
		icon: <SelfImprovementIcon />,
		color: '#FF4081',
		description: 'Managing daily pressures and finding balance',
		package: 'both'
	},
	{
		name: 'Sleeplessness',
		icon: <BedtimeIcon />,
		color: '#4C41FF',
		description: 'Struggling with sleep and rest',
		package: 'both'
	},
	{
		name: 'Negative thoughts',
		icon: <MoodBadIcon />,
		color: '#FF4081',
		description: 'Challenging unhelpful thinking patterns',
		package: 'both'
	},
	{
		name: 'Anxiety',
		icon: <EmojiEmotionsIcon />,
		color: '#4C41FF',
		description: 'Managing worries and anxious feelings',
		package: 'both'
	},
	{
		name: 'Relationship issues',
		icon: <PeopleAltIcon />,
		color: '#FF4081',
		description: 'Navigating challenges in personal connections',
		package: 'both'
	},
	// {
	//   name: 'Not listed issue',
	//   icon: <MoreHorizIcon />,
	//   color: '#4C41FF',
	//   description: 'Something else on your mind?',
	//   package: 'both'
	// },
	{
		name: 'Marital challenges',
		icon: <FavoriteBorderIcon />,
		color: '#FF4081',
		description: 'Working through partnership difficulties',
		package: 'both'
	},
	{
		name: 'Procrastination',
		icon: <AccessTimeIcon />,
		color: '#4C41FF',
		description: 'Overcoming delays and building motivation',
		package: 'advanced'
	},
	{
		name: 'Low confidence',
		icon: <MoodIcon />,
		color: '#FF4081',
		description: 'Building self-esteem and self-assurance',
		package: 'advanced'
	},
	{
		name: 'Loneliness',
		icon: <SentimentDissatisfiedIcon />,
		color: '#4C41FF',
		description: 'Addressing feelings of isolation',
		package: 'advanced'
	}
];

// Animation keyframes
const fadeIn = keyframes`
	from { opacity: 0; transform: translateY(20px); }
	to { opacity: 1; transform: translateY(0); }
`;

const thinkingDots = keyframes`
	0%, 20% { opacity: 0.2; }
	50% { opacity: 1; }
	80%, 100% { opacity: 0.2; }
`;

const pulse = keyframes`
	0% { transform: scale(1); }
	50% { transform: scale(1.05); }
	100% { transform: scale(1); }
`;

const bounce = keyframes`
	0%, 80%, 100% { 
		transform: scale(0); 
	} 
	40% { 
		transform: scale(1); 
	} 
`;

const Converse = () => {
	const userData = JSON.parse(localStorage.getItem('user') || '{}');
	const packageType = userData.package_type || 'basic';
	const category = localStorage.getItem("conserve_category");

	const navigate = useNavigate();
	const [selectedCategory, setSelectedCategory] = useState(null);
	const [currentQuestions, setCurrentQuestions] = useState([]);
	const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
	const [selectedAnswers, setSelectedAnswers] = useState({});
	const [totalScore, setTotalScore] = useState(0);
	const [messages, setMessages] = useState([]);
	const [inputMessage, setInputMessage] = useState('');
	const [showChat, setShowChat] = useState(false);
	const [isQuestionMode, setIsQuestionMode] = useState(false);
	const [isThinking, setIsThinking] = useState(false);
	const [askedQuestions, setAskedQuestions] = useState(new Set());
	const [comfortClicked, setComfortClicked] = useState(false);
	const [randomResponses, setRandomResponses] = useState([]);
	const [closingStatement, setClosingStatement] = useState("");
	const [questionsShown, setQuestionsShown] = useState(0);
	const [open, setOpen] = useState(false);
	const [selectedFeedback, setSelectedFeedback] = useState("");
	const [submittedMCQuestions, setSubmittedMCQuestions] = useState({});
	const [submittedQuestions, setSubmittedQuestions] = useState({});

	const messagesEndRef = useRef(null);
	const theme = useTheme();

	// Auto-scroll to bottom of chat
	useEffect(() => {
		messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
	}, [messages]);

	// Get questions for selected topic
	const getQuestionsForTopic = (topic) => {
		return questionsData.chatbot_questions
			.filter(q => q.topic === topic);
	};

	// Get answers for a question
	const getAnswersForQuestion = (questionId) => {
		return answersData.chatbot_answer.filter(a => a.chatbot_questions_id === questionId);
	};

	// Get comfort statements for a topic
	const getComfortStatementsForTopic = (topic) => {
		console.log("getComfortStatementsForTopic")
		const result = comfortStatementsData.chatbot_comfort_statement.filter(
			(cs) => cs.topic === topic
		);

		console.log("Filtered comfort statements:", result); // ✅ log the value
		return result;
	};

	const getRandomResponsesByCategory = () => {
		// 1️⃣ Get topic from localStorage
		console.log("Saved topic from localStorage:", category);

		if (!category) {
			console.warn("No conserve_category found in localStorage");
			return;
		}

		// 2️⃣ Filter data based on topic
		const filtered = questionsResponseData.chatbot_questions_response.filter(
			(item) => item.topic === category
		);

		if (filtered.length === 0) {
			console.warn("No responses found for this topic");
			return;
		}

		// 3️⃣ Group by category
		const groupedByCategory = filtered.reduce((acc, item) => {
			if (!acc[item.category]) acc[item.category] = [];
			acc[item.category].push(item);
			return acc;
		}, {});

		// 4️⃣ Pick one random response from each category
		const randomResponsesArray = Object.keys(groupedByCategory).map((cat) => {
			const items = groupedByCategory[cat];
			const randomItem = items[Math.floor(Math.random() * items.length)];
			return {
				alias: randomItem?.alias,
				category: cat,
				response: randomItem.response
			};
		});

		// 5️⃣ Store in state
		setRandomResponses(randomResponsesArray);

		// 6️⃣ Log results with topic
		console.log("🎯 Random responses from each category with topic:", randomResponsesArray);

		return randomResponsesArray;
	};


	const getRandomClosingStatement = () => {
		const category = localStorage.getItem("conserve_category");
		console.log("📂 Saved category from localStorage:", category);

		if (!category) {
			console.warn("⚠️ No category found in localStorage");
			return;
		}

		const matchedClosings = closingStatementsData.chatbot_closing_statement.filter(
			(item) => item.topic?.trim().toLowerCase() === category.trim().toLowerCase()
		);

		if (matchedClosings.length === 0) {
			console.warn("⚠️ No closing statements found for topic:", category);
			return;
		}

		const randomIndex = Math.floor(Math.random() * matchedClosings.length);
		const randomClosing = matchedClosings[randomIndex].closingstatement;

		console.log("🎯 Random Closing Statement:", randomClosing);

		// Store in state to render
		setClosingStatement(randomClosing);

		return randomClosing;
	};



	const handleCategorySelect = (category) => {
		localStorage.setItem("conserve_category", category);
		clearSessionData();

		setSelectedCategory(category);
		setShowChat(true);
		setIsQuestionMode(true);
		setCurrentQuestionIndex(0);
		setSelectedAnswers({});
		setTotalScore(0);
		setAskedQuestions(new Set());

		// Get questions for this topic
		const questions = getQuestionsForTopic(category);
		setCurrentQuestions(questions);

		// ✅ Access topic structure dynamically
		const topicData = messageStructureData.topic_message_structure[category];
		if (!topicData) {
			console.error(`No topic data found for category: ${category}`);
			return;
		}

		// ✅ Get category ranges dynamically
		const greetingCategories = topicData.opening_statements?.category_range || [];
		const questionCategories = topicData.questions?.category_range || [];

		// ✅ Pick greeting dynamically (instead of hardcoded 'cat0')
		const greetingQuestions = questions.filter(q => greetingCategories.includes(q.category));
		const randomGreeting = greetingQuestions[Math.floor(Math.random() * greetingQuestions.length)];

		const welcomeMessage = {
			id: Date.now(),
			text: randomGreeting
				? randomGreeting.question
				: `I see you've selected ${category}. Let's begin.`,
			sender: 'bot',
			timestamp: new Date()
		};

		setMessages([welcomeMessage]);

		// ✅ Show thinking animation before first *dynamic* question
		setTimeout(() => {
			showThinkingAnimation();

			setTimeout(() => {
				// ✅ Use first question category dynamically (instead of 'cat1')
				const firstQuestionCategory = questionCategories[0];
				const firstQuestions = questions.filter(q => q.category === firstQuestionCategory);

				if (firstQuestions.length > 0) {
					const randomQuestion = firstQuestions[Math.floor(Math.random() * firstQuestions.length)];
					showQuestion(randomQuestion);
				} else {
					console.warn(`No questions found for category ${firstQuestionCategory}`);
				}
			}, 1000);
		}, 2000);
	};

	// Show question with answer options
	// const showQuestion = (question) => {
	//   // Check if question was already asked
	//   if (askedQuestions.has(question.id)) {
	//     // Skip this question and move to next
	//     setTimeout(() => {
	//       moveToNextQuestion();
	//     }, 100);
	//     return;
	//   }

	//   // Mark question as asked
	//   setAskedQuestions(prev => new Set([...prev, question.id]));

	//   const questionMessage = {
	//     id: Date.now(),
	//     text: question.question,
	//     sender: 'bot',
	//     timestamp: new Date(),
	//     questionData: question,
	//     answers: getAnswersForQuestion(question.id),
	//     category: question.category,
	//     topic: question.topic
	//   };

	//   setMessages(prev => [...prev, questionMessage]);
	// };

	const showQuestion = (question) => {
		if (askedQuestions.has(question.id)) {
			setTimeout(() => moveToNextQuestion(), 100);
			return;
		}

		// Mark question as asked
		setAskedQuestions(prev => new Set([...prev, question.id]));

		// ✅ Increment shown questions
		setQuestionsShown(prev => prev + 1);

		const questionMessage = {
			id: Date.now(),
			text: question.question,
			sender: 'bot',
			timestamp: new Date(),
			questionData: question,
			answers: getAnswersForQuestion(question.id),
			category: question.category,
			topic: question.topic
		};

		setMessages(prev => [...prev, questionMessage]);
	};

	// Get dynamic thinking message based on category
	const getThinkingMessage = (category) => {
		const thinkingMessages = {
			'Burnout': [
				'Analyzing your energy levels...',
				'Understanding your work-life balance...',
				'Processing your stress indicators...',
				'Evaluating your burnout patterns...'
			],
			'Stress': [
				'Assessing your stress levels...',
				'Analyzing your coping mechanisms...',
				'Understanding your pressure points...',
				'Processing your stress responses...'
			],
			'Sleeplessness': [
				'Evaluating your sleep patterns...',
				'Analyzing your rest quality...',
				'Understanding your sleep habits...',
				'Processing your sleep concerns...'
			],
			'Negative thoughts': [
				'Processing your thought patterns...',
				'Analyzing your mental state...',
				'Understanding your mindset...',
				'Evaluating your cognitive patterns...'
			],
			'Anxiety': [
				'Understanding your anxiety triggers...',
				'Analyzing your worry patterns...',
				'Processing your anxious thoughts...',
				'Evaluating your stress responses...'
			],
			'Relationship issues': [
				'Analyzing your relationship dynamics...',
				'Understanding your connection patterns...',
				'Processing your interpersonal concerns...',
				'Evaluating your social interactions...'
			],
			'Marital challenges': [
				'Understanding your partnership dynamics...',
				'Analyzing your relationship patterns...',
				'Processing your marital concerns...',
				'Evaluating your communication styles...'
			],
			'Procrastination': [
				'Analyzing your motivation patterns...',
				'Understanding your productivity blocks...',
				'Processing your time management...',
				'Evaluating your task completion habits...'
			],
			'Low confidence': [
				'Understanding your self-perception...',
				'Analyzing your confidence levels...',
				'Processing your self-esteem patterns...',
				'Evaluating your self-worth indicators...'
			],
			'Loneliness': [
				'Understanding your social connections...',
				'Analyzing your isolation patterns...',
				'Processing your emotional needs...',
				'Evaluating your support systems...'
			]
		};

		const messages = thinkingMessages[category] || [
			'Processing your response...',
			'Analyzing your situation...',
			'Understanding your concerns...',
			'Evaluating your needs...'
		];

		return messages[Math.floor(Math.random() * messages.length)];
	};

	// Show thinking animation
	const showThinkingAnimation = () => {
		setIsThinking(true);
		const dynamicMessage = getThinkingMessage(selectedCategory);

		const thinkingMessage = {
			id: Date.now() + Math.random(),
			text: '',
			sender: 'bot',
			timestamp: new Date(),
			isThinking: true,
			thinkingText: dynamicMessage
		};

		setMessages(prev => [...prev, thinkingMessage]);

		// Remove thinking message after animation
		setTimeout(() => {
			setMessages(prev => prev.filter(msg => !msg.isThinking));
			setIsThinking(false);
		}, 1000);
	};



	const handleAnswerSelect = (questionId, answer) => {
		let scoreToAdd = 0;

		if (Array.isArray(answer)) {
			// ✅ Multiple choice
			answer.forEach(a => {
				const userMessage = {
					id: Date.now() + Math.random(), // ensure unique
					text: a.answer,
					sender: 'user',
					timestamp: new Date()
				};

				setMessages(prev => [...prev, userMessage]);
				scoreToAdd += parseInt(a.score);
			});
		} else {
			// ✅ Single Choice / Yes-No
			const userMessage = {
				id: Date.now(),
				text: answer.answer,
				sender: 'user',
				timestamp: new Date()
			};
			setMessages(prev => [...prev, userMessage]);
			scoreToAdd = parseInt(answer.score);
		}

		// ✅ Store answers
		setSelectedAnswers(prev => ({
			...prev,
			[questionId]: answer
		}));

		// ✅ Update score
		setTotalScore(prev => prev + scoreToAdd);

		// ✅ thinking + next question
		setTimeout(() => {
			showThinkingAnimation();
			setTimeout(() => {
				moveToNextQuestion();
			}, 1000);
		}, 500);
	};

	// Show comfort statement after cat12
	const showComfortStatement = () => {
		const comfortStatements = getComfortStatementsForTopic(selectedCategory);

		if (comfortStatements.length > 0) {
			const randomIndex = Math.floor(Math.random() * comfortStatements.length);
			const comfortStatement = comfortStatements[randomIndex];

			const comfortMessage = {
				id: Date.now(),
				text: comfortStatement.comfortstatement,
				sender: 'bot',
				timestamp: new Date(),
				topic: selectedCategory,
				category: 'comfort',
				isComfortStatement: true
			};

			setMessages(prev => [...prev, comfortMessage]);
		} else {
			// No comfort statement, go directly to responses
			moveToResponsePhase();
		}
	};

	// Handle comfort statement click to proceed to responses
	const handleComfortStatementClick = () => {
		setComfortClicked(true);
		console.log("handleComfortStatementClick")
		// getComfortStatementsForTopic(category);
		moveToResponsePhase();
		getRandomResponsesByCategory();
		getRandomClosingStatement();
	};



	const moveToResponsePhase = () => {
		const category = localStorage.getItem("conserve_category");
		if (!category) {
			console.error("No category found in localStorage.");
			return;
		}

		const topicData = messageStructureData.topic_message_structure[category];
		if (!topicData) {
			console.error(`No topic data found for category: ${category}`);
			return;
		}

		// ✅ Dynamically get first response category
		const responseCategories = topicData.responses?.category_range || [];
		const firstResponseCategory = responseCategories[0];

		if (firstResponseCategory) {
			const responseQuestions = currentQuestions.filter(
				q => q.category === firstResponseCategory && !askedQuestions.has(q.id)
			);

			if (responseQuestions.length > 0) {
				const randomQuestion = responseQuestions[Math.floor(Math.random() * responseQuestions.length)];
				setTimeout(() => showQuestion(randomQuestion), 2000);
			} else {
				// If no question exists in the first response category, skip to closing phase
				showClosingStatement();
			}
		} else {
			// If responses are not defined at all
			showClosingStatement();
		}
	};

	// ✅ Fully dynamic closing phase
	const showClosingStatement = () => {
		const category = localStorage.getItem("conserve_category");
		if (!category) {
			console.error("No category found in localStorage.");
			return;
		}

		const topicData = messageStructureData.topic_message_structure[category];
		if (!topicData) {
			console.error(`No topic data found for category: ${category}`);
			return;
		}

		// ✅ Dynamically get closing categories
		const closingCategories = topicData.closing_statements?.category_range || [];
		const firstClosingCategory = closingCategories[0];

		if (firstClosingCategory) {
			const closingQuestions = currentQuestions.filter(
				q => q.category === firstClosingCategory && !askedQuestions.has(q.id)
			);

			if (closingQuestions.length > 0) {
				const randomQuestion = closingQuestions[Math.floor(Math.random() * closingQuestions.length)];
				showQuestion(randomQuestion);
			} else {
				showResults();
			}
		} else {
			showResults();
		}
	};



	const moveToNextQuestion = () => {
		// const answeredCount = Object.keys(selectedAnswers).length;

		if (!category) {
			console.error("No category found in localStorage.");
			return;
		}

		const topicData = messageStructureData.topic_message_structure[category];
		if (!topicData) {
			console.error(`No topic data found for category: ${category}`);
			return;
		}

		const questionsCount = topicData.questions.count;
		const comfortCount = topicData.comfort_statements.count;
		const responsesCount = topicData.responses.count;
		const closingCount = topicData.closing_statements.count;

		const questionCategories = topicData.questions.category_range;
		const responseCategories = topicData.responses.category_range;
		const closingCategories = topicData.closing_statements.category_range;

		console.log("Questions Count:", topicData.questions.count);
		console.log("Comfort Statements Count:", topicData.comfort_statements.count);
		console.log("Responses Count:", topicData.responses.count);
		console.log("Closing Statements Count:", topicData.closing_statements.count);

		console.log("Question Categories:", topicData.questions.category_range);
		console.log("Response Categories:", topicData.responses.category_range);
		console.log("Closing Categories:", topicData.closing_statements.category_range);


		// ✅ 1️⃣ Questions Phase
		if (questionsShown < questionsCount) {
			let nextCategory = questionCategories[questionsShown];
			let categoryQuestions = currentQuestions?.filter(
				q => q.category === nextCategory && !askedQuestions.has(q.id)
			);

			// ✅ If no questions found, try next available question category
			while (categoryQuestions.length === 0 && questionsShown < questionsCount) {
				nextCategory = questionCategories.find(cat =>
					currentQuestions.some(q => q.category === cat && !askedQuestions.has(q.id))
				);

				if (!nextCategory) break;
				categoryQuestions = currentQuestions.filter(
					q => q.category === nextCategory && !askedQuestions.has(q.id)
				);
			}

			if (categoryQuestions.length > 0) {
				const randomIndex = Math.floor(Math.random() * categoryQuestions.length);
				const nextQuestion = categoryQuestions[randomIndex];
				setTimeout(() => showQuestion(nextQuestion), 2000);
				return;
			}

			// ✅ If none left, go to comfort phase
			setTimeout(() => showComfortStatement(), 2000);
			return;
		}

		// ✅ 2️⃣ Comfort Phase
		else if (questionsShown === questionsCount) {
			setTimeout(() => showComfortStatement(), 2000);
			return;
		}

		// ✅ 3️⃣ Response Phase
		else if (questionsShown < questionsCount + comfortCount + responsesCount) {
			let responseIndex = questionsShown - questionsCount - comfortCount;
			let nextCategory = responseCategories[responseIndex];

			let categoryQuestions = currentQuestions.filter(
				q => q.category === nextCategory && !askedQuestions.has(q.id)
			);

			// ✅ Try next available response category if empty
			while (categoryQuestions.length === 0 && responseIndex < responsesCount) {
				nextCategory = responseCategories.find(cat =>
					currentQuestions.some(q => q.category === cat && !askedQuestions.has(q.id))
				);

				if (!nextCategory) break;
				categoryQuestions = currentQuestions.filter(
					q => q.category === nextCategory && !askedQuestions.has(q.id)
				);
			}

			if (categoryQuestions.length > 0) {
				const randomIndex = Math.floor(Math.random() * categoryQuestions.length);
				const nextQuestion = categoryQuestions[randomIndex];
				setTimeout(() => showQuestion(nextQuestion), 2000);
				return;
			}

			// ✅ If none left, go to closing
			setTimeout(() => showClosingStatement(), 2000);
			return;
		}

		// ✅ 4️⃣ Closing Phase
		else if (questionsShown < questionsCount + comfortCount + responsesCount + closingCount) {
			setTimeout(() => showClosingStatement(), 2000);
			return;
		}

		// ✅ End of flow
		showResults();
	};

	// Get current category based on answered questions
	const getCurrentCategory = () => {
		const answeredCount = Object.keys(selectedAnswers).length;

		// Questions phase: cat2 to cat12 (11 questions total)
		if (answeredCount < 11) {
			return `cat${answeredCount + 2}`; // cat2, cat3, ..., cat12
		}

		// Response phase: cat13 to cat20 (8 responses)
		const responseIndex = answeredCount - 11;
		if (responseIndex < 8) {
			return `cat${responseIndex + 13}`; // cat13, cat14, ..., cat20
		}

		// Closing phase: cat21
		return 'cat21';
	};

	// Get next question/response - pick one random from current category, then move to next
	const getNextQuestion = (category) => {
		const categoryQuestions = currentQuestions.filter(q => q.category === category && !askedQuestions.has(q.id));

		// If there are questions in current category, pick one randomly
		if (categoryQuestions.length > 0) {
			const randomIndex = Math.floor(Math.random() * categoryQuestions.length);
			return categoryQuestions[randomIndex];
		}

		const currentCatNum = parseInt(category.replace('cat', ''));

		// Questions phase: cat2 -> cat3 -> ... -> cat12
		if (currentCatNum >= 2 && currentCatNum < 12) {
			const nextCatNum = currentCatNum + 1;
			const nextCategory = `cat${nextCatNum}`;
			const nextCategoryQuestions = currentQuestions.filter(q => q.category === nextCategory && !askedQuestions.has(q.id));

			if (nextCategoryQuestions.length > 0) {
				const randomIndex = Math.floor(Math.random() * nextCategoryQuestions.length);
				return nextCategoryQuestions[randomIndex];
			}
		}

		// After cat12, move to responses: cat13 -> cat14 -> ... -> cat20
		if (currentCatNum === 12) {
			const nextCategoryQuestions = currentQuestions.filter(q => q.category === 'cat13' && !askedQuestions.has(q.id));
			if (nextCategoryQuestions.length > 0) {
				const randomIndex = Math.floor(Math.random() * nextCategoryQuestions.length);
				return nextCategoryQuestions[randomIndex];
			}
		}

		// Response phase: cat13 -> cat14 -> ... -> cat20
		if (currentCatNum >= 13 && currentCatNum < 20) {
			const nextCatNum = currentCatNum + 1;
			const nextCategory = `cat${nextCatNum}`;
			const nextCategoryQuestions = currentQuestions.filter(q => q.category === nextCategory && !askedQuestions.has(q.id));

			if (nextCategoryQuestions.length > 0) {
				const randomIndex = Math.floor(Math.random() * nextCategoryQuestions.length);
				return nextCategoryQuestions[randomIndex];
			}
		}

		// After cat20, move to closing: cat21
		if (currentCatNum === 20) {
			const nextCategoryQuestions = currentQuestions.filter(q => q.category === 'cat21' && !askedQuestions.has(q.id));
			if (nextCategoryQuestions.length > 0) {
				const randomIndex = Math.floor(Math.random() * nextCategoryQuestions.length);
				return nextCategoryQuestions[randomIndex];
			}
		}

		return null;
	};

	// Clear session data from local storage
	const clearSessionData = () => {
		try {
			// Clear all chatbot-related data from localStorage
			localStorage.removeItem('chatbot_session');
			localStorage.removeItem('chatbot_answers');
			localStorage.removeItem('chatbot_score');
			localStorage.removeItem('chatbot_category');
			localStorage.removeItem('chatbot_messages');
			localStorage.removeItem('chatbot_asked_questions');

			// Clear any other session-related data
			Object.keys(localStorage).forEach(key => {
				if (key.startsWith('chatbot_') || key.startsWith('echo_')) {
					localStorage.removeItem(key);
				}
			});
		} catch (error) {
			console.log('Error clearing localStorage:', error);
		}
	};

	// Show final results (after cat21 closing statement)
	const showResults = () => {
		// Cat21 closing statement will be the final message
		// No additional result message needed as cat21 handles closing
		setIsQuestionMode(false);

		// Clear local storage when session ends
		setTimeout(() => {
			clearSessionData();
		}, 1000);
	};

	const handleQuestionSelect = (question) => {
		setShowChat(true);

		// Add user's question to chat
		const newMessage = {
			id: Date.now(),
			text: question,
			sender: 'user',
			timestamp: new Date()
		};

		setMessages(prev => [...prev, newMessage]);

		// Simulate bot response
		setTimeout(() => {
			const botResponse = {
				id: Date.now() + 1,
				text: getBotResponse(question, selectedCategory),
				sender: 'bot',
				timestamp: new Date()
			};
			setMessages(prev => [...prev, botResponse]);
		}, 1000);
	};

	const handleSendMessage = (e) => {
		e.preventDefault();
		if (!inputMessage.trim()) return;

		// Add user message
		const newMessage = {
			id: Date.now(),
			text: inputMessage,
			sender: 'user',
			timestamp: new Date()
		};

		setMessages(prev => [...prev, newMessage]);
		setInputMessage('');

		// Simulate bot response
		setTimeout(() => {
			const botResponse = {
				id: Date.now() + 1,
				text: getBotResponse(inputMessage, selectedCategory),
				sender: 'bot',
				timestamp: new Date()
			};
			setMessages(prev => [...prev, botResponse]);
		}, 1000);
	};

	const getBotResponse = (message, category) => {
		// This is a simple response generator - in a real app, you'd call an API
		const responses = {
			'Burnout': [
				"I understand that burnout can be really challenging. Let's work through this together.",
				"It sounds like you're feeling overwhelmed. Would you like to explore some stress-reduction techniques?"
			],
			'Stress': [
				"Stress can be really tough to manage. Let's find some strategies that work for you.",
				"I hear that you're feeling stressed. Would you like to try a quick breathing exercise?"
			],
			// Add more category-based responses
			'default': [
				"I'm here to help. Could you tell me more about how you're feeling?",
				"That's interesting. Can you elaborate on that?",
				"I understand. How has this been affecting you?"
			]
		};

		const categoryResponses = responses[category] || responses['default'];
		return categoryResponses[Math.floor(Math.random() * categoryResponses.length)];
	};

	const handleBackToCategories = () => {
		setShowChat(false);
		setMessages([]);
		setComfortClicked(false);
		setQuestionsShown(0);           // Reset counter
		setAskedQuestions(new Set());

		// ✅ Reset all answers
		setSelectedAnswers({});
		setSubmittedMCQuestions({});
		setSubmittedQuestions({});      // if you track Y/N or SC submissions separately
		setTotalScore(0);
	};

	const handleClickOpen = () => {
		setOpen(true);
	};

	const handleClose = () => {
		setOpen(false);
		navigate('/home');

		if (selectedFeedback === "yes") {
			navigate("/consult");
		} else if (selectedFeedback === "no") {
			navigate("/home");
		}
	};
	return (
		<Box sx={{ background: 'linear-gradient(135deg, #f8fffe 0%, #f5f3ff 100%)', }}>
			<Container maxWidth={false} disableGutters sx={{
				minHeight: '100vh',
				background: 'linear-gradient(135deg, #f8fffe 0%, #f5f3ff 100%)',
			}}>
				{!showChat ? (
					// Categories Selection View
					<Box
						sx={{
							width: '100%',
							px: { xs: 2, sm: 4 },
							py: 2
						}}
					>
						{/* Header Section */}
						<Box sx={{ mb: 4 }}>
							{/* <Box
								component="img"
								src="/chatbot-assets/chatbot-icon.svg"
								alt="MindNest AI Companion"
								sx={{
									width: 100,
									height: 100,
									mb: 1,
									borderRadius: '50%',
									boxShadow: '0 8px 32px rgba(139, 103, 227, 0.3)'
								}}
							/> */}

							{/* Header with App Branding */}
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3, justifyContent: "space-between" }}>
							<Typography variant="h6" sx={{
								background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)',
								WebkitBackgroundClip: 'text',
								WebkitTextFillColor: 'transparent',
								fontWeight: 700,
							}}>
								echoBuddy AI Companion
							</Typography>
							<IconButton
								onClick={() => navigate(-1)}
								sx={{
									backgroundColor: '#F0F5FF',
									width: 40,
									height: 40,
									borderRadius: '50%',
									color: '#0B2D6B',
									'&:hover': {
										backgroundColor: '#E0EBFF'
									}
								}}
							>
								<ArrowBackIcon />
							</IconButton>
						</Box>

						<Typography
							variant="h6"
							sx={{
								mb: 1,
								fontWeight: 300,
								color: '#103680',
								fontSize: { xs: '16px', sm: '20px' }
							}}
						>
							How can I help you today?
						</Typography>

							<Typography
								variant="body1"
								sx={{
									color: '#6A73A8',
									fontSize: { xs: '14px', sm: '16px' }
								}}
							>
								Select a category that best describes what you're experiencing, and I'll provide personalized support and guidance.
							</Typography>
						</Box>

						{/* Categories Grid */}
						<Grid
							container
							spacing={2}
							justifyContent="center"
							sx={{ width: '100%' }}
						>
							{categories
								.filter(category => {
									const showCategory =
										packageType === 'advanced' ||
										category.package === 'both' ||
										category.package === 'basic';
									// console.log(`Category: ${category.name}, Package: ${category.package}, Show: ${showCategory}`);
									return showCategory;
								})
								.map((category, index) => (
									<Grid
										item
										xs={12}
										sm={4}
										md={3}
										lg={3}
										key={category.name}
										sx={{
											display: 'flex',
											opacity: 0,
											animation: `${fadeIn} 0.6s ease-out forwards`,
											animationDelay: `${index * 0.1}s`,
											height: { xs: 'auto', sm: 185 },
											width: '100%',
											maxWidth: { xs: 'none', sm: 250 },
										}}
									>
										<Card
											onClick={() => handleCategorySelect(category.name)}
											sx={{
												width: '100%',
												height: '100%',
												cursor: 'pointer',
												transition: 'all 0.3s ease',
												background: '#ffffff',
												border: '1px solid #eef0f5',
												borderRadius: 3,
												display: 'flex',
												flexDirection: 'column',
												alignItems: 'center',
												justifyContent: 'flex-start',
												padding: 2,
												paddingY: 1,
												gap: 0.8,
												overflow: 'hidden',
												position: 'relative',
												minHeight: { xs: 'auto', sm: '170px' },
												maxHeight: { xs: 'none', sm: '170px' },
												boxShadow: '0 4px 16px rgba(106, 115, 168, 0.12)',
												'&::before': {
													content: '""',
													position: 'absolute',
													top: 0,
													left: 0,
													right: 0,
													height: '4px',
													background: `linear-gradient(90deg, ${category.color}, #14b8a6)`,
													opacity: 0,
													transition: 'opacity 0.3s ease'
												},
												'&:hover': {
													transform: 'translateY(-6px)',
													boxShadow: `0 12px 28px ${category.color}33`,
													borderColor: category.color,
													'&::before': {
														opacity: 1
													}
												}
											}}
										>
											<Box
												sx={{
													display: 'flex',
													flexDirection: 'column',
													alignItems: 'center',
													textAlign: 'center',
													p: { xs: 2, sm: 1 },
													gap: { xs: 1, sm: 0 },
													maxHeight: { xs: 'none', sm: '170px' },
													minHeight: { xs: 'auto', sm: '170px' },
													justifyContent: 'space-evenly'
												}}
											>
												<Box
													sx={{
														width: 52,
														height: 52,
														borderRadius: '14px',
														background: `linear-gradient(135deg, ${category.color}, #14b8a6)`,
														display: 'flex',
														alignItems: 'center',
														justifyContent: 'center',
														flexShrink: 0,
														boxShadow: `0 6px 16px ${category.color}40`
													}}
												>
													{React.cloneElement(category.icon, {
														sx: { fontSize: 26, color: '#ffffff' }
													})}
												</Box>

												<Typography
													variant="subtitle1"
													sx={{
														fontWeight: 800,
														color: category.color,
														fontSize: { xs: '15px', sm: '17px' },
													}}
												>
													{category.name}
												</Typography>

												<Typography
													variant="body2"
													sx={{
														color: '#8A8A8E',
														lineHeight: 1.4,
														fontSize: { xs: '13px', sm: '14px' },
														textAlign: 'center',
														overflow: 'hidden',
														textOverflow: 'ellipsis',
														display: '-webkit-box',
														WebkitLineClamp: { xs: 'unset', sm: 3 },
														WebkitBoxOrient: 'vertical',
														minHeight: { xs: 'auto', sm: '58px' },
														maxHeight: { xs: 'none', sm: '58px' },
														mt: 0.5,
														mb: 0.5
													}}
												>
													{category.description}
												</Typography>
											</Box>
										</Card>
									</Grid>
								))}
						</Grid>
					</Box>
				) : (
					// Chat Interface
					<Slide direction="up" in={showChat} mountOnEnter unmountOnExit>
						<Paper
							elevation={0}
							sx={{
								width: '100%',
								maxWidth: 900,
								height: '80vh',
								display: 'flex',
								flexDirection: 'column',
								borderRadius: 3,
								overflow: 'hidden',
								mx: 'auto',
								background: 'rgba(255, 255, 255, 0.95)',
								backdropFilter: 'blur(20px)',
								border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
							}}
						>
							{/* Chat Header */}
							<Box
								sx={{
									backgroundColor: '#fff',
									color: '#103680',
									p: 2,
									display: 'flex',
									alignItems: 'center',
									gap: 2,
									borderBottom: '1px solid #eaeef5',
									boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
								}}
							>
								<IconButton
									onClick={handleBackToCategories}
									sx={{
										color: '#103680',
										'&:hover': {
											bgcolor: 'rgba(16, 54, 128, 0.1)'
										}
									}}
								>
									<ArrowBackIcon />
								</IconButton>

								<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
									<Box
										component="img"
										src="/chatbot-assets/chatbot-icon.svg"
										alt="AI Assistant"
										sx={{
											width: 33,
											height: 33,
											borderRadius: '50%'
										}}
									/>
									<Box>
										<Typography variant="h6" sx={{ fontWeight: 600, color: '#103680' }}>
											{selectedCategory} Support
										</Typography>
										<Typography variant="caption" sx={{ color: '#8B67E3' }}>
											AI Assistant <Box component="span" sx={{ color: 'green' }}>•</Box> Online
										</Typography>
									</Box>
								</Box>
							</Box>

							{/* Messages */}
							<Box
								sx={{
									flex: 1,
									p: 2,
									overflowY: 'auto',
									backgroundColor: '#f8f9fa'
								}}
							>
								{comfortClicked && randomResponses.length > 0 ? (
									<>
										{/* 🧠 Random responses cards */}
										{randomResponses.map((item, index) => (
											<Box
												key={index}
												sx={{
													maxWidth: "100%",
													p: 2,
													borderRadius: 2,
													bgcolor: "#ffffff",
													color: "#2c3e50",
													boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
													border: "1px solid #eaeef5",
													mb: 2,
												}}
											>
												<Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
													{item?.alias}
												</Typography>
												<Typography variant="body1">
													{item.response}
												</Typography>
											</Box>
										))}

										{/* {comfortClicked && randomResponses.length > 0 && (
										<Box
											sx={{
												maxWidth: "100%",
												p: 2,
												borderRadius: 2,
												bgcolor: "#ffffff", // similar to previous card background
												color: "#2c3e50",
												boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
												border: "1px solid #eaeef5",
												mb: 2,
											}}
										>
											{randomResponses.map((response, index) => (
												<Typography
													key={index}
													variant="body1"
													color="text.secondary"
													sx={{ mb: 1 }}
												>
													{response}
												</Typography>
											))}
										</Box>
									)} */}

										{/* ✅ Closing Statement card */}
										{closingStatement && (
											<Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
												<Box
													sx={{
														display: "inline-block",
														p: 2,
														borderRadius: 2,
														bgcolor: "#ffffff",
														color: "#2c3e50",
														boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
														border: "1px solid #eaeef5",
													}}
												>
													<Typography variant="body1" sx={{ mb: 1 }}>
														{closingStatement}
													</Typography>

													{/* Yes / No buttons */}
													<Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
														<Button
															size="small"
															variant={selectedFeedback === "yes" ? "contained" : "outlined"}
															onClick={() => {
																setSelectedFeedback("yes");
																handleClickOpen(); // ✅ call your function here
															}}
															sx={{
																borderColor: "green",
																color: selectedFeedback === "yes" ? "#fff" : "green",
																backgroundColor: selectedFeedback === "yes" ? "green" : "transparent",
																px: 0.5,
																py: 0.25,
																fontSize: "0.65rem",
																minWidth: "30px",
																"&:hover": {
																	borderColor: "green",
																	backgroundColor: selectedFeedback === "yes" ? "darkgreen" : "rgba(0,128,0,0.1)",
																},
															}}
														>
															Yes
														</Button>

														<Button
															size="small"
															variant={selectedFeedback === "no" ? "contained" : "outlined"}
															onClick={() => {
																setSelectedFeedback("no");
																handleClickOpen(); // ✅ call your function here
															}}
															sx={{
																borderColor: "red",
																color: selectedFeedback === "no" ? "#fff" : "red",
																backgroundColor: selectedFeedback === "no" ? "red" : "transparent",
																px: 0.5,
																py: 0.25,
																fontSize: "0.65rem",
																minWidth: "30px",
																"&:hover": {
																	borderColor: "red",
																	backgroundColor: selectedFeedback === "no" ? "darkred" : "rgba(255,0,0,0.1)",
																},
															}}
														>
															No
														</Button>
													</Box>

												</Box>
											</Box>
										)}

										<Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
											<DialogTitle>
												{selectedFeedback === "yes" ? "🎉 Glad to have you!" : ""}
											</DialogTitle>
											<DialogContent>
												<Typography variant="body1">
													{selectedFeedback === "yes"
														? "We’re glad you have adopted Echo as your buddy. Just follow the suggested recommendations — stay healthy and happy with Echo!"
														: "“We hear you. Don’t worry — you can talk to one of our counsellors for support when ever ready.”"}
												</Typography>
											</DialogContent>
											<DialogActions>
												<Button onClick={handleClose} variant="contained" color="primary">
													OK
												</Button>
											</DialogActions>
										</Dialog>

									</>
								) : (
									<>
										{messages.map((message) => (
											<Box
												key={message.id}
												sx={{
													display: "flex",
													justifyContent:
														message.sender === "user" ? "flex-end" : "flex-start",
													mb: 2,
												}}
											>
												{message.sender === "bot" && !message.isThinking && (
													<Box
														component="img"
														src="/chatbot-assets/chatbot-icon.svg"
														alt="Chatbot"
														sx={{
															width: 36,
															height: 36,
															borderRadius: "50%",
															backgroundColor: `rgba(106, 115, 168, 0.1)`,
															display: "flex",
															alignItems: "center",
															justifyContent: "center",
															mr: 2,
															flexShrink: 0,
														}}
													/>
												)}

												{/* 💬 Message bubble rendering logic remains the same */}
												<Box
													sx={{
														maxWidth: "80%",
														p: 2,
														borderRadius: 2,
														bgcolor:
															message.sender === "user"
																? "#6A73A8"
																: message.isThinking
																	? "transparent"
																	: "#ffffff",
														color: message.sender === "user" ? "white" : "#2c3e50",
														boxShadow: message.isThinking
															? "none"
															: "0 4px 20px rgba(0,0,0,0.05)",
														border:
															message.sender === "bot" && !message.isThinking
																? "1px solid #eaeef5"
																: "none",
													}}
												>
													{/* Show thinking animation */}
													{message.isThinking ? (
														<Box sx={{
															display: 'flex',
															alignItems: 'center',
															p: 1
														}}>
															<Box sx={{
																mr: 1.5,
																flexShrink: 0
															}}>
																<Box sx={{
																	animation: `${pulse} 2s ease-in-out infinite`,
																	mr: 0.5,
																	display: 'flex',
																	alignItems: 'center'
																}}>
																	<img src="/chatbot-assets/chatbot-icon.svg" alt="Chatbot" style={{ width: '28px', height: '28px' }} />
																</Box>
																<Box sx={{ display: 'flex', gap: 0.4, alignItems: 'center' }}>
																	{[0, 1, 2].map((dot) => (
																		<Box
																			key={dot}
																			sx={{
																				width: 5,
																				height: 5,
																				borderRadius: '50%',
																				bgcolor: '#6A73A8',
																				animation: `${bounce} 1.6s ease-in-out ${dot * 0.3}s infinite`,
																				opacity: 0.8
																			}}
																		/>
																	))}
																</Box>
															</Box>
															<Typography sx={{
																fontStyle: 'italic',
																color: '#6b7c93',
																fontSize: '0.85rem',
																fontWeight: 400
															}}>
																{message.thinkingText || 'Processing your response...'}
															</Typography>
														</Box>
													) : (
														<>
															{/* Show category and topic info temporarily */}
															{/* {(message.category || message.topic) && (
													<Box sx={{
														mb: 1,
														p: 1,
														bgcolor: '#f0f0f0',
														borderRadius: 1,
														fontSize: '12px',
														color: '#666'
													}}>
														{message.topic && <span>Topic: {message.topic}</span>}
														{message.category && <span> | Category: {message.category}</span>}
														{message.questionData && <span> | Type: {message.questionData.question_type}</span>}
														{message.questionData && <span> | Q#: {message.questionData.question_number}</span>}
													</Box>
												)} */}

															{message.isComfortStatement ? (
																<Box
																	onClick={handleComfortStatementClick}
																	sx={{
																		background: 'linear-gradient(135deg, #6A73A8 0%, #103680 100%)',
																		color: 'white',
																		p: 3,
																		borderRadius: 3,
																		cursor: 'pointer',
																		boxShadow: '0 8px 32px rgba(106, 115, 168, 0.3)',
																		border: '2px solid rgba(255,255,255,0.2)',
																		position: 'relative',
																		overflow: 'hidden',
																		transition: 'all 0.3s ease',
																		'&:hover': {
																			transform: 'translateY(-2px)',
																			boxShadow: '0 12px 40px rgba(106, 115, 168, 0.4)',
																		},
																		'&::before': {
																			content: '""',
																			position: 'absolute',
																			top: 0,
																			left: '-100%',
																			width: '100%',
																			height: '100%',
																			background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
																			animation: 'shimmer 2s infinite',
																		},
																		'@keyframes shimmer': {
																			'0%': { left: '-100%' },
																			'100%': { left: '100%' }
																		}
																	}}
																>
																	<Typography variant="h6" sx={{
																		fontWeight: 600,
																		mb: 2,
																		textAlign: 'center'
																	}}>
																		💙 Comfort & Support
																	</Typography>
																	<Typography variant="body1" sx={{
																		lineHeight: 1.6,
																		textAlign: 'center',
																		mb: 2
																	}}>
																		{message.text}
																	</Typography>
																	<Box sx={{
																		textAlign: 'center',
																		mt: 2,
																		p: 2,
																		bgcolor: 'rgba(255,255,255,0.1)',
																		borderRadius: 2,
																		border: '1px solid rgba(255,255,255,0.2)'
																	}}>
																		<Typography variant="body2" sx={{
																			fontStyle: 'italic',
																			opacity: 0.9
																		}}>
																			Click to explore personalized solutions and guidance
																		</Typography>
																	</Box>
																</Box>
															) : (
																<Typography variant="body1" sx={{ lineHeight: 1.6 }}>
																	{message.text}
																</Typography>
															)}

															{/* Render answer options for questions */}
															{message.questionData && message.answers && (
																<Box sx={{ mt: 2 }}>
																	{message.questionData.question_type === 'YN' && (
																		<Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
																			{message.answers.map((answer) => (
																				<Button
																					key={answer.id}
																					variant="outlined"
																					size="small"
																					onClick={() => {
																						handleAnswerSelect(message.questionData.id, answer);

																						// Mark this question as submitted
																						setSubmittedQuestions(prev => ({
																							...prev,
																							[message.questionData.id]: true
																						}));
																					}}
																					disabled={submittedQuestions[message.questionData.id]}
																					sx={{
																						borderColor: '#6A73A8',
																						color: '#6A73A8',
																						'&:hover': {
																							bgcolor: '#6A73A8',
																							color: 'white'
																						}
																					}}
																				>
																					{answer.answer}
																				</Button>
																			))}
																		</Box>
																	)}

																	{message.questionData.question_type === 'SC' && (
																		<FormControl disabled={!!selectedAnswers[message.questionData.id]}>
																			<RadioGroup
																				value={selectedAnswers[message.questionData.id]?.id || ""}
																			>
																				{message.answers.map((answer) => (
																					<FormControlLabel
																						key={answer.id}
																						value={answer.answer}
																						control={
																							<Radio
																								size="small"
																								onClick={(e) => {
																									e.stopPropagation(); // ✅ Prevent parent row click
																									handleAnswerSelect(message.questionData.id, answer);
																								}}
																							/>
																						}
																						label={answer.answer}
																						// onClick={() => handleAnswerSelect(message.questionData.id, answer)}
																						sx={{
																							'& .MuiFormControlLabel-label': {
																								fontSize: '14px',
																								cursor: 'pointer'
																							}
																						}}
																					/>
																				))}
																			</RadioGroup>
																		</FormControl>
																	)}

																	{(message.questionData.question_type === 'MC' || message.questionData.question_type === 'MM') && (
																		// <FormControl component="fieldset" sx={{ mt: 1, width: '100%' }}>
																		// 	{message.answers.map((answer) => (
																		// 		<FormControlLabel
																		// 			key={answer.id}
																		// 			control={
																		// 				<Checkbox
																		// 					checked={selectedAnswers[message.questionData.id]?.includes(answer.id) || false}
																		// 					onChange={(e) => {
																		// 						const questionId = message.questionData.id;
																		// 						const answerId = answer.id;
																		// 						const currentSelections = selectedAnswers[questionId] || [];

																		// 						let newSelections;
																		// 						if (e.target.checked) {
																		// 							newSelections = [...currentSelections, answerId];
																		// 						} else {
																		// 							newSelections = currentSelections.filter(id => id !== answerId);
																		// 						}

																		// 						setSelectedAnswers(prev => ({
																		// 							...prev,
																		// 							[questionId]: newSelections
																		// 						}));
																		// 					}}
																		// 					sx={{
																		// 						color: '#6A73A8',
																		// 						'&.Mui-checked': {
																		// 							color: '#6A73A8',
																		// 						},
																		// 					}}
																		// 				/>
																		// 			}
																		// 			label={answer.answer}
																		// 			sx={{
																		// 				width: '100%',
																		// 				margin: '4px 0',
																		// 				'& .MuiFormControlLabel-label': {
																		// 					fontSize: '0.9rem',
																		// 					lineHeight: 1.3
																		// 				}
																		// 			}}
																		// 		/>
																		// 	))}
																		// 	{!submittedMCQuestions[message.questionData.id] && (
																		// 		<Box sx={{ mt: 2, textAlign: 'center' }}>
																		// 			<Button
																		// 				variant="contained"
																		// 				size="small"
																		// 				onClick={() => {
																		// 					handleAnswerSelect(
																		// 						message.questionData,
																		// 						selectedAnswers[message.questionData.id] || []
																		// 					);

																		// 					// Hide submit button after click
																		// 					setSubmittedMCQuestions(prev => ({
																		// 						...prev,
																		// 						[message.questionData.id]: true
																		// 					}));
																		// 				}}
																		// 				disabled={!selectedAnswers[message.questionData.id] || selectedAnswers[message.questionData.id].length === 0}
																		// 				sx={{
																		// 					bgcolor: '#6A73A8',
																		// 					'&:hover': {
																		// 						bgcolor: '#103680'
																		// 					},
																		// 					'&:disabled': {
																		// 						bgcolor: '#ccc'
																		// 					}
																		// 				}}
																		// 			>
																		// 				Submit
																		// 			</Button>
																		// 		</Box>
																		// 	)}
																		// </FormControl>
																		<FormControl component="fieldset" sx={{ mt: 1, width: '100%' }}>
																			{message.answers.map((answer) => (
																				<FormControlLabel
																					key={answer.id}
																					control={
																						<Checkbox
																							disabled={submittedMCQuestions[message.questionData.id]}   // ✅ Disable when submitted
																							// checked={
																							// 	submittedMCQuestions[message.questionData.id]
																							// 		? false // ✅ Always unselected after submit
																							// 		: selectedAnswers[message.questionData.id]?.includes(answer.id) || false
																							// }
																							checked={
																								submittedMCQuestions[message.questionData.id]
																									? false // ✅ After submit, always uncheck
																									: selectedAnswers[message.questionData.id]?.some(a => a.id === answer.id) || false
																							}
																							// onChange={(e) => {
																							// 	// ✅ Still allow change only before submit
																							// 	if (submittedMCQuestions[message.questionData.id]) return;

																							// 	const questionId = message.questionData.id;
																							// 	const answerId = answer.id;
																							// 	const currentSelections = selectedAnswers[questionId] || [];

																							// 	let newSelections;
																							// 	if (e.target.checked) {
																							// 		newSelections = [...currentSelections, answerId];
																							// 	} else {
																							// 		newSelections = currentSelections.filter(id => id !== answerId);
																							// 	}

																							// 	setSelectedAnswers(prev => ({
																							// 		...prev,
																							// 		[questionId]: newSelections
																							// 	}));
																							// }}
																							onChange={(e) => {
																								if (submittedMCQuestions[message.questionData.id]) return;

																								const questionId = message.questionData.id;
																								const answerObj = answer; // full object
																								const currentSelections = selectedAnswers[questionId] || [];

																								let newSelections;
																								if (e.target.checked) {
																									newSelections = [...currentSelections, answerObj]; // store object
																								} else {
																									newSelections = currentSelections.filter(a => a.id !== answerObj.id);
																								}

																								setSelectedAnswers(prev => ({
																									...prev,
																									[questionId]: newSelections
																								}));
																							}}
																							sx={{
																								color: '#6A73A8',
																								'&.Mui-checked': {
																									color: '#6A73A8',
																								},
																							}}
																						/>
																					}
																					label={answer.answer}
																					sx={{
																						width: '100%',
																						margin: '4px 0',
																						'& .MuiFormControlLabel-label': {
																							fontSize: '0.9rem',
																							lineHeight: 1.3
																						}
																					}}
																				/>
																			))}

																			{/* ✅ Show Submit ONLY if not yet submitted */}
																			{!submittedMCQuestions[message.questionData.id] && (
																				<Box sx={{ mt: 2, textAlign: 'center' }}>
																					<Button
																						variant="contained"
																						size="small"
																						onClick={() => {
																							// ✅ Trigger the existing logic
																							handleAnswerSelect(
																								message.questionData,
																								selectedAnswers[message.questionData.id] || []
																							);

																							// ✅ Mark question as submitted
																							setSubmittedMCQuestions(prev => ({
																								...prev,
																								[message.questionData.id]: true
																							}));
																						}}
																						disabled={
																							!selectedAnswers[message.questionData.id] ||
																							selectedAnswers[message.questionData.id].length === 0
																						}
																						sx={{
																							bgcolor: '#6A73A8',
																							'&:hover': {
																								bgcolor: '#103680'
																							},
																							'&:disabled': {
																								bgcolor: '#ccc'
																							}
																						}}
																					>
																						Submit
																					</Button>
																				</Box>
																			)}
																		</FormControl>
																	)}

																	{message.questionData.question_type === 'MS' && (

																		<FormControl component="fieldset" sx={{ mt: 1, width: '100%' }}>
																			{message.answers.map((answer) => (
																				<FormControlLabel
																					key={answer.id}
																					control={
																						<Checkbox
																							disabled={submittedMCQuestions[message.questionData.id]}   // ✅ Disable when submitted
																							// checked={
																							// 	submittedMCQuestions[message.questionData.id]
																							// 		? false // ✅ Always unselected after submit
																							// 		: selectedAnswers[message.questionData.id]?.includes(answer.id) || false
																							// }
																							checked={
																								submittedMCQuestions[message.questionData.id]
																									? false // ✅ After submit, always uncheck
																									: selectedAnswers[message.questionData.id]?.some(a => a.id === answer.id) || false
																							}												// }}
																							onChange={(e) => {
																								if (submittedMCQuestions[message.questionData.id]) return;

																								const questionId = message.questionData.id;
																								const answerObj = answer; // full object
																								const currentSelections = selectedAnswers[questionId] || [];

																								let newSelections;
																								if (e.target.checked) {
																									newSelections = [...currentSelections, answerObj]; // store object
																								} else {
																									newSelections = currentSelections.filter(a => a.id !== answerObj.id);
																								}

																								setSelectedAnswers(prev => ({
																									...prev,
																									[questionId]: newSelections
																								}));
																							}}
																							sx={{
																								color: '#6A73A8',
																								'&.Mui-checked': {
																									color: '#6A73A8',
																								},
																							}}
																						/>
																					}
																					label={answer.answer}
																					sx={{
																						width: '100%',
																						margin: '4px 0',
																						'& .MuiFormControlLabel-label': {
																							fontSize: '0.9rem',
																							lineHeight: 1.3
																						}
																					}}
																				/>
																			))}

																			{/* ✅ Show Submit ONLY if not yet submitted */}
																			{!submittedMCQuestions[message.questionData.id] && (
																				<Box sx={{ mt: 2, textAlign: 'center' }}>
																					<Button
																						variant="contained"
																						size="small"
																						onClick={() => {
																							// ✅ Trigger the existing logic
																							handleAnswerSelect(
																								message.questionData,
																								selectedAnswers[message.questionData.id] || []
																							);

																							// ✅ Mark question as submitted
																							setSubmittedMCQuestions(prev => ({
																								...prev,
																								[message.questionData.id]: true
																							}));
																						}}
																						disabled={
																							!selectedAnswers[message.questionData.id] ||
																							selectedAnswers[message.questionData.id].length === 0
																						}
																						sx={{
																							bgcolor: '#6A73A8',
																							'&:hover': {
																								bgcolor: '#103680'
																							},
																							'&:disabled': {
																								bgcolor: '#ccc'
																							}
																						}}
																					>
																						Submit
																					</Button>
																				</Box>
																			)}
																		</FormControl>

																	)}
																</Box>
															)}

														</>
													)}
												</Box>
											</Box>
										))}
									</>
								)}
								<div ref={messagesEndRef} />
							</Box>

						</Paper>
					</Slide>
				)}
			</Container>
		</Box>
	);
};

export default Converse;
